import { CustomFieldService, ICustomFieldSchema } from '@/api/customField';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { DeleteCustomField } from '@/components/customField/DeleteCustomField';
import { CreateButton } from '@/components/shared/button/CreateButton';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { Button, CustomCellProperties } from 'alurkerja-ui';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { CustomOptionsModal } from './CustomOptionsModal';
import { AddRemoveProjectModal } from './AddRemoveProject';
import { ProjectService } from '@/api/project';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { FaGear, FaPen } from 'react-icons/fa6';
import { FaCheck } from 'react-icons/fa';
import { IoMdCloseCircle } from 'react-icons/io';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';
import { ISelectOption } from '@/components/form/fields/SelectField';

interface CustomActionCellProps {
  data: ICustomFieldSchema;
  onToggleEnable: (
    id: string,
    isEnable: boolean,
    data: ICustomFieldSchema,
  ) => void;
  onSettingClick: (data: ICustomFieldSchema) => void;
  projectData: ISelectOption[];
  tableRef: React.RefObject<ExtendedTableLowCodeRef>;
}

const CustomActionCell: React.FC<CustomActionCellProps> = ({
  data,
  onToggleEnable,
  onSettingClick,
  projectData,
  tableRef,
}) => {
  return (
    <>
      <LinkButton path={`${data.id}/edit`} icon={<FaPen size={15} />} />

      <DeleteCustomField
        id={data.id!}
        onSuccess={() => tableRef.current?.refetch()}
      />

      <AddRemoveProjectModal
        id={data.id!}
        customFieldData={{ ...data }}
        projectData={projectData}
        onSuccess={() => tableRef.current?.refetch()}
      />
      <Button onClick={() => onSettingClick(data)} className="bg-gray-800">
        <FaGear />
      </Button>
      {data.is_enable === true ? (
        <Button
          onClick={() => onToggleEnable(data.id!, false, data)} // Ubah menjadi false
          className="bg-green-300"
        >
          <FaCheck size={15} className="text-green-900" />
        </Button>
      ) : (
        <Button
          onClick={() => onToggleEnable(data.id!, true, data)} // Ubah menjadi true
          className="bg-red-500"
        >
          <IoMdCloseCircle size={15} />
        </Button>
      )}
    </>
  );
};

export const ListCustomField: React.FC = () => {
  const { id } = useParams();
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  const { data: optionsData } = CustomFieldService.useGetOptions();
  const filterSpec: IFilterSpec[] = [
    { name: 'label_field', label: 'Custom Label', type: 'text' },
    {
      name: 'module_type',
      label: 'Module Type',
      type: 'option',
      options: optionsData?.module_type,
    },
    {
      name: 'custom_field_type',
      label: 'Custom Input Type',
      type: 'option',
      options: optionsData?.custom_field_type,
    },
    { name: 'created_at', label: 'Created Date', type: 'date' },
  ];

  const spec = useCustomSpec('/api/crud/custom-field', {
    columns: [
      { key: 'module_type', label: 'Module Type' },
      { key: 'label_field', label: 'Custom Label' },
      { key: 'custom_field_type', label: 'Custom Input Type' },
      { key: 'created_at', label: 'Created Date' },
    ],
  });

  const specCustomOptions = useCustomSpec('/api/crud/custom-option', {
    columns: [{ key: 'option', label: 'Option Name' }],
  });

  const { data: projectData, isFetching: isProjectFetching } =
    ProjectService.useGetProjectOption();

  const { mutate } = CustomFieldService.useUpdate();

  const handleToggleEnable = (
    id: string,
    isEnable: boolean,
    data: ICustomFieldSchema,
  ) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to ${isEnable ? 'Enable' : 'Disable'} this field?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${isEnable ? 'Enable' : 'Disable'} it!`,
    }).then(result => {
      if (result.isConfirmed) {
        const updatedData = {
          id,
          is_enable: isEnable,
          module_type: data.module_type,
          custom_field_type: data.custom_field_type,
          label_field: data.label_field,
          is_mandatory: data.is_mandatory,
          is_searchable: data.is_searchable,
          project_ids: data.project_ids,
        };

        mutate(updatedData, {
          onSuccess: () => {
            Swal.fire(
              'Success',
              `Field has been ${
                isEnable ? 'enabled' : 'disabled'
              } successfully`,
              'success',
            ).then(() => {
              ref.current?.refetch();
            });
          },
          onError: () => {
            Swal.fire(
              'Error',
              `Failed to ${isEnable ? 'enable' : 'disable'} the field`,
              'error',
            );
          },
        });
      }
    });
  };

  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedData, setSelectedData] = useState<ICustomFieldSchema | null>(
    null,
  );

  const handleSettingsClick = (data: ICustomFieldSchema) => {
    setSelectedData(data);
    setModalVisible(true);
  };

  const customCell = ({ name, value, defaultCell }: CustomCellProperties) => {
    if (name === 'created_at') {
      return <>{moment(value).format('DD MMMM YYYY')}</>;
    }
    return defaultCell;
  };

  if (isProjectFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <Section
      title="Custom Fields"
      actions={<CreateButton label="Add Fields" />}
    >
      <ExtendedTableLowCode
        ref={ref}
        defaultFilter={{
          project_id: id,
        }}
        defaultSort="name"
        defaultSortOrder="desc"
        spec={spec}
        customButtonCreate={HideCustomCreateButton}
        customCell={customCell}
        customActionCell={data => (
          <CustomActionCell
            data={data as ICustomFieldSchema}
            onToggleEnable={handleToggleEnable}
            onSettingClick={handleSettingsClick}
            projectData={projectData}
            tableRef={ref}
          />
        )}
        filterSpec={filterSpec}
        showFilter
      />
      {selectedData && (
        <CustomOptionsModal
          visible={isModalVisible}
          onHide={() => setModalVisible(false)}
          data={selectedData}
          id={selectedData.id ?? ''}
          spec={specCustomOptions ?? {}}
          customCell={customCell ?? {}}
        />
      )}
    </Section>
  );
};
